import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import http from '../../api/http';
import { getApiPermissionGroups, saveRoleApiGroupPermissions } from '../../api/apiCenter';

const { Text, Paragraph } = Typography;

export default function ApiPermissionPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [draftPermissions, setDraftPermissions] = useState({});

  const activeRole = useMemo(() => roles.find((role) => role.id === activeRoleId) || null, [roles, activeRoleId]);

  const readRoleGroupPermissions = (role, permissionGroups) => {
    const permNames = new Set((role?.permissions || []).map((perm) => perm.name));
    return permissionGroups.reduce((acc, group) => {
      acc[group.key] = {
        view: permNames.has(group.viewPermission) || permNames.has(group.testPermission),
        test: permNames.has(group.testPermission)
      };
      return acc;
    }, {});
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: rolesData }, groupsData] = await Promise.all([
        http.get('/management/roles'),
        getApiPermissionGroups()
      ]);
      const roleRecords = rolesData || [];
      const groupRecords = groupsData?.groups || [];
      setRoles(roleRecords);
      setGroups(groupRecords);
      const defaultRole = roleRecords.find((role) => role.id === activeRoleId) || roleRecords[0] || null;
      setActiveRoleId(defaultRole?.id || null);
      if (defaultRole) {
        setDraftPermissions(readRoleGroupPermissions(defaultRole, groupRecords));
      }
    } catch (error) {
      message.error(error.message || '加载 API 分组权限失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeRole) {
      setDraftPermissions(readRoleGroupPermissions(activeRole, groups));
    }
  }, [activeRole, groups]);

  const handleToggle = (groupKey, field, checked) => {
    setDraftPermissions((current) => {
      const base = current[groupKey] || { view: false, test: false };
      const next = { ...base, [field]: checked };
      if (field === 'test' && checked) next.view = true;
      if (field === 'view' && !checked) next.test = false;
      return { ...current, [groupKey]: next };
    });
  };

  const handleSave = async () => {
    if (!activeRole) {
      message.warning('请先选择角色');
      return;
    }
    try {
      setSaving(true);
      const groupPermissions = groups.map((group) => ({
        key: group.key,
        view: !!draftPermissions[group.key]?.view,
        test: !!draftPermissions[group.key]?.test
      }));
      await saveRoleApiGroupPermissions(activeRole.id, groupPermissions);
      message.success('API 分组权限已保存');
      await loadData();
    } catch (error) {
      message.error(error.message || '保存 API 分组权限失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="这里管理的是 API 中心的分组查看/测试权限。页面入口权限 manage_api_test 与管理权限 manage_api_permissions 仍保留在原有权限设置页。"
        />

        <Card loading={loading} title="角色选择" extra={<Button onClick={loadData}>刷新</Button>}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              placeholder="选择角色"
              value={activeRoleId}
              onChange={setActiveRoleId}
              options={roles.map((role) => ({ value: role.id, label: `${role.name} (${role.description || '未填写说明'})` }))}
              style={{ width: 360 }}
            />
            {activeRole ? (
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                当前角色：<Text strong>{activeRole.name}</Text>
              </Paragraph>
            ) : null}
          </Space>
        </Card>

        <Card
          loading={loading}
          title="API 分组权限"
          extra={<Button type="primary" loading={saving} onClick={handleSave} disabled={!activeRole}>保存分组权限</Button>}
        >
          {activeRole ? (
            <Table
              rowKey="key"
              pagination={false}
              dataSource={groups}
              columns={[
                {
                  title: '分组',
                  dataIndex: 'title',
                  render: (_, record) => (
                    <Space direction="vertical" size={2}>
                      <Space>
                        <Text strong>{record.title}</Text>
                        <Tag>{record.endpointCount} 个接口</Tag>
                      </Space>
                      <Text type="secondary">{record.description}</Text>
                    </Space>
                  )
                },
                {
                  title: '查看权限',
                  width: 120,
                  render: (_, record) => (
                    <Switch
                      checked={!!draftPermissions[record.key]?.view}
                      onChange={(checked) => handleToggle(record.key, 'view', checked)}
                      disabled={activeRole.name === 'Admin'}
                    />
                  )
                },
                {
                  title: '测试权限',
                  width: 120,
                  render: (_, record) => (
                    <Switch
                      checked={!!draftPermissions[record.key]?.test}
                      onChange={(checked) => handleToggle(record.key, 'test', checked)}
                      disabled={activeRole.name === 'Admin'}
                    />
                  )
                },
                {
                  title: '权限编码',
                  width: 340,
                  render: (_, record) => (
                    <Space direction="vertical" size={2}>
                      <Text type="secondary">{record.viewPermission}</Text>
                      <Text type="secondary">{record.testPermission}</Text>
                    </Space>
                  )
                }
              ]}
            />
          ) : <Empty description="请先选择一个角色" />}
        </Card>
      </Space>
    </div>
  );
}